import { type FunctionComponent } from 'react'
import { Helmet } from 'react-helmet-async'
import { ButtonLink, PageTitle, TypographyV2 } from '@which/seatbelt'

import { useFreeAccountExclusiveDataQuery } from '../../../generated/frontend'
import { ErrorComponent } from '../../../shared/components/Error'
import { Loader } from '../../../shared/components/Loader'
import { FreeAccountExclusive } from '../../home/components/FreeAccountExclusive'
import { accountPagesDataLayer } from '../utils'
import styles from './FreeAccountPage.module.scss'

const WelcomePage: FunctionComponent = () => {
  const { loading, error, data } = useFreeAccountExclusiveDataQuery()

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  const FALLBACK_IMAGE = {
    src: 'https://media.which.gpp.io/prod/images/ar_2to1_700x350/0876149fc87b-ecoverbathroom-cleaner-500mlwh23917-0032-01.webp',
    alt: 'Ecover Bathroom Cleaner Spray Mint & Cucumber',
    sources: [{ media: '', sizes: 'two-to-one', srcset: '', type: '' }],
    renditions: [],
    caption: '',
    dimensions: {
      height: '350',
      width: '700',
    },
    id: 'ecover-bathroom-cleaner-spray-mint-cucumber',
  }

  const freeAccountExclusive = data?.homePage?.freeAccountExclusive || {
    title: "Your free fortnightly recommendation — it's on us",
    description:
      'For this selection, our editor has picked a Best Buy cleaning spray our expert testers loved.',
    image: FALLBACK_IMAGE,
  }

  const imageWithSources = {
    ...freeAccountExclusive.image,
    renditions: freeAccountExclusive.image?.renditions || [],
  }

  return (
    <section className={styles.welcomePage}>
      <Helmet>
        <script>
          {accountPagesDataLayer({
            vertical: 'signup',
            content_type: 'signup',
            contentGroup: 'free_account/complete',
          })}
        </script>
      </Helmet>
      <div className={styles.centre}>
        <PageTitle
          pageTitle="Welcome to your Which? free account"
          pageTitleTag="h1"
          standfirst="You’re logged in and ready to go!"
        />
        <div className={styles.redRectangle} aria-hidden="true" />
      </div>
      <div className={styles.wrapper}>
        <div className={styles.explore}>
          <TypographyV2 textStyle="sb-text-heading-large" tag="h2" className={styles.heading}>
            Own your weekly shop
          </TypographyV2>
          <TypographyV2 textStyle="sb-text-body-default-regular">
            You now have instant access to our curated selection of free reviews for the everyday
            essentials that matter most.
          </TypographyV2>
          <TypographyV2 textStyle="sb-text-body-default-regular">
            Whether you’re hunting for the best-tasting sourdough or the shops and supermarkets that
            offer the best value, we've done the testing so you can save money and buy the best.
          </TypographyV2>
          <ButtonLink href="/" appearance="primary" className={styles.button}>
            See what you’ve unlocked
          </ButtonLink>
        </div>
        <div className={styles.reviews}>
          <TypographyV2 textStyle="sb-text-heading-medium" tag="h2" className={styles.heading}>
            Your first fortnightly review
          </TypographyV2>
          <TypographyV2 textStyle="sb-text-body-default-regular">
            If you’ve opted in, send you a review comparing two expert-selected products, delivered
            to your inbox every fortnight.
          </TypographyV2>
          <TypographyV2 textStyle="sb-text-body-default-regular">
            See how this week's products compare in the latest review.
          </TypographyV2>
        </div>
        <div className={styles.freeAccountExclusiveWrapper}>
          <FreeAccountExclusive
            title={freeAccountExclusive.title}
            description={freeAccountExclusive.description}
            image={imageWithSources}
            forceShow={true}
          />
        </div>
      </div>
    </section>
  )
}

export default WelcomePage
